import { FC, UIEvent, useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Box, CircularProgress, Popover, Stack, Typography, styled } from "@mui/material";
import { background, text, blue } from "@/theme/colors";
import { EMPTY_STR } from "@/constants/string";
import { TGetJobListReqDto } from "@/api/services/main/job/dtos/get-job-list.dto";
import { getJobIdsWithPagesApi } from "@/api/services/main/job";
import { TJobIdWithPageDto } from "@/api/services/main/job/dtos/get-job-ids-with-pages.dto";
import { showError } from "@/utils/error";

export type TSelectedJobPage = {
  id: string;
  jobName: string;
  page: number;
};

export type TJobSelectionProps = {
  selectedCount: number;
  query: TGetJobListReqDto;
  getJobSelected: () => number[];
  isSelectAll: boolean;
  excludedIds: number[];
};

export const SelectedJobPagesPopover: FC<TJobSelectionProps> = ({ selectedCount, query, getJobSelected, isSelectAll, excludedIds }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dropdownData, setDropdownData] = useState<TSelectedJobPage[]>([]);
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [dropdownHasMore, setDropdownHasMore] = useState<boolean>(false);
  const [isDropdownLoading, setIsDropdownLoading] = useState<boolean>(false);
  const [isFetchingCount, setIsFetchingCount] = useState<boolean>(false);
  const [internalMatchingCount, setInternalMatchingCount] = useState<number>(selectedCount);
  const prevSelectedCountRef = useRef<number>(selectedCount);
  const isMountedRef = useRef<boolean>(true);

  // Sync internalMatchingCount when selectedCount changes
  useEffect(() => {
    if (selectedCount !== prevSelectedCountRef.current) {
      const diff = selectedCount - prevSelectedCountRef.current;
      if (!isSelectAll) {
        setInternalMatchingCount((prev) => Math.max(0, prev + diff));
      }
      prevSelectedCountRef.current = selectedCount;
    }
  }, [selectedCount, isSelectAll]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const matchingCount = isSelectAll ? selectedCount : Math.min(selectedCount, internalMatchingCount);
  const open = Boolean(anchorEl);

  // Extract only filter fields (exclude page and perPage) to avoid fetching on pagination
  const searchOptionsStr = useMemo(() => {
    const { page: _page, perPage: _perPage, ...filters } = query;
    return JSON.stringify(filters);
  }, [query]);

  // Store refs for values needed in effect but shouldn't trigger re-fetch
  const getJobSelectedRef = useRef(getJobSelected);
  const queryRef = useRef(query);
  const selectedCountRef = useRef(selectedCount);

  // Keep refs updated
  useEffect(() => {
    getJobSelectedRef.current = getJobSelected;
    queryRef.current = query;
    selectedCountRef.current = selectedCount;
  });

  // Fetch matching count ONLY when search filter options change
  useEffect(() => {
    if (isSelectAll) return;

    const fetchMatchingCount = async () => {
      const jobSelected = getJobSelectedRef.current();
      if (jobSelected.length === 0) {
        setInternalMatchingCount(0);
        return;
      }

      setIsFetchingCount(true);
      try {
        const currentQuery = queryRef.current;
        const res = await getJobIdsWithPagesApi({
          ...currentQuery,
          isSelectAll: false,
          jobIds: jobSelected.map(String),
          page: 1,
          perPage: 10,
          jobPerPage: currentQuery.perPage || 10,
          sorts: currentQuery.sorts,
        });
        if (isMountedRef.current) {
          setInternalMatchingCount(res.totalCount);
        }
      } catch (error) {
        if (isMountedRef.current) {
          setInternalMatchingCount(selectedCountRef.current);
        }
      } finally {
        if (isMountedRef.current) {
          setIsFetchingCount(false);
        }
      }
    };

    fetchMatchingCount();
  }, [searchOptionsStr, isSelectAll]);

  const handleClickSelectedCount = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      if (isDropdownLoading) return;
      const currentTarget = event.currentTarget;
      const jobSelected = getJobSelected();
      try {
        if (!isSelectAll && jobSelected.length === 0) return;
        setIsDropdownLoading(true);
        const res = await getJobIdsWithPagesApi({
          ...query,
          isSelectAll,
          excludedIds: excludedIds.map(String),
          jobIds: isSelectAll ? [] : jobSelected.map(String),
          page: 1,
          perPage: 10,
          jobPerPage: query.perPage || 10,
          sorts: query.sorts,
        });

        if (!isMountedRef.current) return;

        const popoverData: TSelectedJobPage[] = res.data.map((item: TJobIdWithPageDto) => ({
          id: item.id,
          jobName: item.jobName,
          page: item.page,
        }));

        setDropdownData(popoverData);
        setDropdownPage(1);
        setDropdownHasMore(popoverData.length < res.totalCount);
        setAnchorEl(currentTarget);
      } catch (error) {
        showError(error);
      } finally {
        if (isMountedRef.current) {
          setIsDropdownLoading(false);
        }
      }
    },
    [isDropdownLoading, getJobSelected, isSelectAll, query, excludedIds],
  );

  const handleLoadMoreDropdown = useCallback(async () => {
    if (isDropdownLoading || !dropdownHasMore) return;
    const jobSelected = getJobSelected();
    try {
      setIsDropdownLoading(true);
      const nextPage = dropdownPage + 1;
      const res = await getJobIdsWithPagesApi({
        ...query,
        isSelectAll,
        excludedIds: excludedIds.map(String),
        jobIds: isSelectAll ? [] : jobSelected.map(String),
        page: nextPage,
        perPage: 10,
        jobPerPage: query.perPage || 10,
        sorts: query.sorts,
      });

      if (!isMountedRef.current) return;

      const newPopoverData: TSelectedJobPage[] = res.data.map((item: TJobIdWithPageDto) => ({
        id: item.id,
        jobName: item.jobName,
        page: item.page,
      }));

      setDropdownData((prev) => {
        const merged = [...prev, ...newPopoverData];
        setDropdownHasMore(merged.length < res.totalCount);
        return merged;
      });
      setDropdownPage(nextPage);
    } catch (error) {
      showError(error);
    } finally {
      if (isMountedRef.current) {
        setIsDropdownLoading(false);
      }
    }
  }, [isDropdownLoading, dropdownHasMore, getJobSelected, dropdownPage, query, isSelectAll, excludedIds]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && dropdownHasMore && !isDropdownLoading) {
      handleLoadMoreDropdown();
    }
  };

  const onClose = () => setAnchorEl(null);

  return (
    <Stack direction="row" alignItems="center">
      <Typography variant="body14Semi" color={text.secondary}>
        チェックした求人：
      </Typography>
      <CountTypography
        variant="body14Semi"
        onClick={matchingCount > 0 ? handleClickSelectedCount : undefined}
        $isClickable={matchingCount > 0}
        $isFetching={isFetchingCount || isDropdownLoading}
      >
        {matchingCount || EMPTY_STR.TEXT}
      </CountTypography>
      <Typography variant="body14Semi">&nbsp;件</Typography>

      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <PopoverContainer onScroll={handleScroll}>
          <Stack>
            {dropdownData.map((job) => (
              <JobItemRow key={job.id}>
                <JobNameTypography variant="body14">{job.jobName || EMPTY_STR.TEXT}</JobNameTypography>
                <PageNumberTypography variant="cap12">({String(job.page).padStart(2, "0")}ページ)</PageNumberTypography>
              </JobItemRow>
            ))}
            {!isDropdownLoading && dropdownData.length === 0 && <EmptyMessageTypography variant="body14">データがありません</EmptyMessageTypography>}
            {isDropdownLoading && (
              <LoadingBox>
                <CircularProgress size={24} />
              </LoadingBox>
            )}
          </Stack>
        </PopoverContainer>
      </StyledPopover>
    </Stack>
  );
};

const CountTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$isClickable" && prop !== "$isFetching",
})<{ $isClickable: boolean; $isFetching?: boolean }>`
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  color: ${({ $isClickable }) => ($isClickable ? blue[80] : "inherit")};
  opacity: ${({ $isFetching }) => ($isFetching ? 0.5 : 1)};
  transition: opacity 0.2s;
  &:hover {
    text-decoration: ${({ $isClickable }) => ($isClickable ? "underline" : "none")};
  }
`;

const StyledPopover = styled(Popover)`
  & .MuiPaper-root {
    margin-top: 8px;
    padding: 0;
    border-radius: 8px;
    box-shadow: 0px 8px 24px 0px #00000026;
  }
`;

const JobItemRow = styled(Stack)`
  min-height: 50px;
  justify-content: center;
  padding: 4px 0;
`;

const JobNameTypography = styled(Typography)`
  color: ${text.primary};
  word-break: break-word;
`;

const PageNumberTypography = styled(Typography)`
  color: ${text.tertiary};
`;

const EmptyMessageTypography = styled(Typography)`
  color: ${text.secondary};
  text-align: center;
  padding-top: 16px;
  padding-bottom: 16px;
`;

const LoadingBox = styled(Box)`
  display: flex;
  justify-content: center;
  padding-top: 8px;
  padding-bottom: 8px;
`;

const PopoverContainer = styled(Box)`
  width: 240px;
  max-height: 320px;
  overflow-y: auto;
  padding: 16px;
  background-color: ${background.white};

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #c0c0c0;
  }
`;
